/* eslint-disable no-useless-escape */
import isEqual from "lodash.isequal";
import { observable, computed, action, makeAutoObservable } from "mobx";
import { User } from "react-netlify-identity";
import { beamwidthColors, defaultColors } from "./constants/colors";
import { GEOMETRY, HORN_TYPES, THROAT_EXIT } from "./constants/enums";
import { IDesign, IHornDesignResponse, IHornDesignRequest } from "./interfaces";
import { toFixedInches } from "./utils/toFixedInches";

type HornType = {
  allowDiffractionSlot: boolean;
  prioritizeLoading: boolean;
};

type AlertDialog = {
  title: string;
  message: string;
  error: string;
};
const dev = window.location.hostname === "localhost";
class Store {
  @observable public allowDiffractionSlot: boolean = false;
  @observable public prioritizeLoading: boolean = false;
  @observable public driverName: string = "CDX1-1070";
  @observable public upperFrequency: number = dev ? 20000 : 0;
  @observable public horizontalBeamWidth: number = dev ? 50 : 0;
  @observable public horizontalMaxSize: number = dev ? 500 : 0;
  @observable public verticalBeamWidth: number = dev ? 80 : 0;
  @observable public verticalMaxSize: number = dev ? 800 : 0;
  @observable public maxLength: number = dev ? 200 : 0;
  @observable public geometry: number = 1;
  @observable public selectedHornType: number = HORN_TYPES.NO_DIFFRACTION_SLOT;
  @observable public selectedThroatExit: number = 0;

  @action resetState = () => {
    this.allowDiffractionSlot = false;
    this.prioritizeLoading = false;
    this.driverName = "CDX1-1070";
    this.upperFrequency = dev ? 20000 : 0;
    this.horizontalBeamWidth = dev ? 50 : 0;
    this.horizontalMaxSize = dev ? 500 : 0;
    this.verticalBeamWidth = dev ? 80 : 0;
    this.verticalMaxSize = dev ? 800 : 0;
    this.maxLength = dev ? 200 : 0;
    this.geometry = 1;
    this.selectedHornType = HORN_TYPES.NO_DIFFRACTION_SLOT;
    this.selectedThroatExit = 0;
  };

  @computed public get throatExit() {
    return Object.keys(THROAT_EXIT)
      .filter((_, index) => index === this.selectedThroatExit)
      .reduce((_, curr) => THROAT_EXIT[curr], "");
  }

  @computed public get hornTypeSelector(): HornType {
    this.verticalMaxSize =
      HORN_TYPES.PRIORITISE_LOADING === this.selectedHornType
        ? 0
        : this.verticalMaxSize;

    this.maxLength =
      HORN_TYPES.PRIORITISE_LOADING === this.selectedHornType
        ? 0
        : this.maxLength;

    return {
      allowDiffractionSlot:
        this.selectedHornType === HORN_TYPES.ALLOW_DIFFRACTION_SLOT,
      prioritizeLoading:
        this.selectedHornType === HORN_TYPES.PRIORITISE_LOADING,
    };
  }

  @computed public get imperialHorizontalMaxSize() {
    return toFixedInches(this.horizontalMaxSize);
  }
  @computed public get imperialVerticalMaxSize() {
    return toFixedInches(this.verticalMaxSize);
  }

  @computed public get imperialMaxLength() {
    return toFixedInches(this.maxLength);
  }

  @observable public customerName: string = "";
  @observable public inputEmail: string = "";
  @observable public joinMailingList = true;
  @observable public password: string = "";
  @observable public errors: string[] = [];

  @computed public get results() {
    const result = this.selectedDesign?.result;
    if (!result) return null;
    return typeof result === "string" ? JSON.parse(result) : result;
  }
  @computed public selectedDesign: IDesign;
  @observable public appLoading: boolean = false;
  @observable public showAlert = false;
  @observable public alertDialog: AlertDialog;

  @observable public user: User = null;
  @observable public myDesigns: IDesign[] = null;
  @observable public selectedDesigns: IDesign[] = [];

  @observable public isImperial = false;

  @observable x = 100;

  @action setX = (newx) => (this.x = newx);

  @action changeAlertState = () => (this.showAlert = !this.showAlert);
  @action setErrors = (errors) => (this.errors = errors);
  @action setUser = (user) => (this.user = user);
  @action clearSelectedDesigns = () => (this.selectedDesigns = []);

  constructor() {
    makeAutoObservable(this);
    const imperialString = localStorage.getItem("isImperial");
    this.isImperial = imperialString === "true" || false;
  }

  @action public toggleImperial = () => {
    this.isImperial = !this.isImperial;
    localStorage.setItem("isImperial", this.isImperial.toString());
  };

  @action public toggleJoinMailing = () => {
    this.joinMailingList = !this.joinMailingList;
  };

  @action public handleChange = (name: string, value: number) => {
    this[name] = value;
  };

  @action public increment = (name: string, scale: number) => {
    this[name] = Number(this[name]) + scale;
  };

  @action public decrement = (name: string, scale: number) => {
    this[name] = Number(this[name]) - scale;
  };

  @action resetDesigns = () => {
    this.selectedDesigns = [];
    this.myDesigns = [];
    this.clearLocalDesigns();
    this.inputEmail = "";
  };

  @computed public get email() {
    return this.user?.email || "";
  }

  @computed public get missingRequiredParams() {
    const requiredParamsMap = {
      [GEOMETRY.Circular]: {
        [HORN_TYPES.NO_DIFFRACTION_SLOT]: {
          requiredParams: [
            this.horizontalBeamWidth,
            this.verticalBeamWidth,
            this.horizontalMaxSize,
            this.verticalMaxSize,
            this.upperFrequency,
          ],
        },
        [HORN_TYPES.ALLOW_DIFFRACTION_SLOT]: {
          requiredParams: [
            this.horizontalBeamWidth,
            this.verticalBeamWidth,
            this.horizontalMaxSize,
            this.verticalMaxSize,
            this.upperFrequency,
          ],
        },
        [HORN_TYPES.PRIORITISE_LOADING]: {
          requiredParams: [
            this.horizontalBeamWidth,
            this.verticalBeamWidth,
            this.horizontalMaxSize,
            this.upperFrequency,
          ],
        },
      },
      [GEOMETRY.Elliptical]: {
        [HORN_TYPES.NO_DIFFRACTION_SLOT]: {
          requiredParams: [
            this.horizontalBeamWidth,
            this.verticalBeamWidth,
            this.horizontalMaxSize,
            this.verticalMaxSize,
            this.upperFrequency,
          ],
        },
        [HORN_TYPES.ALLOW_DIFFRACTION_SLOT]: {
          requiredParams: [
            this.horizontalBeamWidth,
            this.verticalBeamWidth,
            this.horizontalMaxSize,
            this.verticalMaxSize,
            this.upperFrequency,
          ],
        },
        [HORN_TYPES.PRIORITISE_LOADING]: {
          requiredParams: [
            this.horizontalBeamWidth,
            this.verticalBeamWidth,
            this.horizontalMaxSize,
            this.upperFrequency,
          ],
        },
      },
      [GEOMETRY.Rectangular]: {
        [HORN_TYPES.NO_DIFFRACTION_SLOT]: {
          requiredParams: [
            this.horizontalBeamWidth,
            this.verticalBeamWidth,
            this.horizontalMaxSize,
            this.verticalMaxSize,
            this.upperFrequency,
          ],
        },
        [HORN_TYPES.ALLOW_DIFFRACTION_SLOT]: {
          requiredParams: [
            this.horizontalBeamWidth,
            this.verticalBeamWidth,
            this.horizontalMaxSize,
            this.verticalMaxSize,
            this.upperFrequency,
          ],
        },
        [HORN_TYPES.PRIORITISE_LOADING]: {
          requiredParams: [
            this.horizontalBeamWidth,
            this.verticalBeamWidth,
            this.horizontalMaxSize,
            this.upperFrequency,
          ],
        },
      },
    };

    const requiredParamsSelected =
      requiredParamsMap?.[this?.geometry]?.[this?.selectedHornType]
        ?.requiredParams;
    if (!requiredParamsSelected) {
      return true;
    }
    return requiredParamsSelected.some((v) => v === 0);
  }

  @computed public get isEmailValid() {
    //eslint-disable-next-line
    const regex =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(String(this.inputEmail).toLowerCase());
  }

  private getFrequencyXPosition = (data, amount: number) =>
    data.find((item) => item > amount);

  private getFrequencylabels(data) {
    return [
      {
        label: "250",
        number: this.getFrequencyXPosition(data, 250),
      },
      {
        label: "500",
        number: this.getFrequencyXPosition(data, 500),
      },
      {
        label: "1k",
        number: this.getFrequencyXPosition(data, 1000),
      },
      {
        label: "2k",
        number: this.getFrequencyXPosition(data, 2000),
      },
      {
        label: "5k",
        number: this.getFrequencyXPosition(data, 5000),
      },
      {
        label: "10k",
        number: this.getFrequencyXPosition(data, 10000),
      },
      {
        label: "20k",
        // data is exclusive of 20K so set tick at 1 less
        number: this.getFrequencyXPosition(data, 19999),
      },
    ];
  }

  @computed public get beamwidth() {
    if (this.results) {
      const data = this.models
        .reduce((acc, { model }, currIndex) => {
          let horizontal = [];
          let vertical = [];

          model.frequencies.forEach((value, index) => {
            horizontal.push({ x: value, y: model.beamwidthHor[index] });
            vertical.push({ x: value, y: model.beamwidthVert[index] });
          });

          return [
            ...acc,
            {
              id: this.hasSingleModel
                ? "horizontal"
                : `Design ${currIndex + 1} H`,
              data: horizontal,
              color: beamwidthColors[2 * currIndex],
            },
            {
              id: this.hasSingleModel
                ? "vertical"
                : `Design ${currIndex + 1} V`,
              data: vertical,
              color: beamwidthColors[2 * currIndex + 1],
            },
          ];
        }, [])
        .reverse();

      return {
        data,
        xAxisLabels: this.getFrequencylabels(this.results.frequencies),
      };
    }
    return { data: [], xAxisLabels: [] };
  }

  @computed public get directivity() {
    if (this.results) {
      const data = this.models
        .reduce((acc, { model }, currIndex) => {
          let directivityPlots = [];

          model.frequencies.forEach((value, index) => {
            directivityPlots.push({ x: value, y: model.DI[index] });
          });

          return [
            ...acc,
            {
              id: this.hasSingleModel
                ? "directivity index"
                : `Design ${currIndex + 1}`,
              data: directivityPlots,
              color: defaultColors[currIndex],
            },
          ];
        }, [])
        .reverse();

      return {
        data,
        xAxisLabels: this.getFrequencylabels(this.results.frequencies),
      };
    }
    return { data: [], xAxisLabels: [] };
  }

  @computed public get impedance() {
    if (this.results) {
      const data = this.models
        .reduce((acc, { model }, currIndex) => {
          let impedancePlots = [];

          model.frequencies.forEach((value, index) => {
            impedancePlots.push({ x: value, y: model.Ze[index] });
          });

          return [
            ...acc,
            {
              id: this.hasSingleModel ? "impedance" : `Design ${currIndex + 1}`,
              data: impedancePlots,
              color: defaultColors[currIndex],
            },
          ];
        }, [])
        .reverse();

      return {
        data,
        xAxisLabels: this.getFrequencylabels(this.results.frequencies),
      };
    }
    return { data: [], xAxisLabels: [] };
  }

  @computed public get frequencyResponse() {
    if (this.results) {
      const data = this.models
        .reduce((acc, { model }, currIndex) => {
          let frequencyResponsePlots = [];

          model.frequencies.forEach((value, index) => {
            frequencyResponsePlots.push({ x: value, y: model.SPL[index] });
          });

          return [
            ...acc,
            {
              id: this.hasSingleModel
                ? "frequency response"
                : `Design ${currIndex + 1}`,
              data: frequencyResponsePlots,
              color: defaultColors[currIndex],
            },
          ];
        }, [])
        .reverse();

      return {
        data,
        xAxisLabels: this.getFrequencylabels(this.results.frequencies),
      };
    }
    return { data: [], xAxisLabels: [] };
  }

  @computed public get params(): IHornDesignRequest {
    const isCircular = this.geometry === GEOMETRY.Circular;

    const verticalBeamWidth = isCircular
      ? this.horizontalBeamWidth
      : this.verticalBeamWidth;

    const verticalMaxSize =
      (isCircular ? this.horizontalMaxSize : this.verticalMaxSize) / 1000;

    return {
      ...this.hornTypeSelector,
      driverName: this.driverName,
      horizontalBeamWidth: this.horizontalBeamWidth,
      horizontalMaxSize: this.horizontalMaxSize / 1000,
      upperFrequency: this.upperFrequency,
      verticalBeamWidth,
      verticalMaxSize,
      maxLength: this.maxLength / 1000,
      geometry: this.geometry,
    };
  }

  @action public setDesign = async (design: IDesign) => {
    this.selectedDesign = design;
    Object.keys(design.parameters).forEach((key) => {
      this[key] = design.parameters[key];
    });
  };

  public clearLocalDesigns = () => sessionStorage.removeItem("models");

  public getLocalDesigns = () => {
    return (
      (JSON.parse(sessionStorage.getItem("models")) || []) as IDesign[]
    ).map((design) => {
      const parameters = {
        ...design.parameters,
        verticalMaxSize: design.parameters.verticalMaxSize * 1000,
        horizontalMaxSize: design.parameters.horizontalMaxSize * 1000,
        maxLength: design.parameters.maxLength * 1000,
      };
      return { ...design, parameters };
    });
  };

  public getServerDesigns = async () => {
    const response = await fetch(".netlify/functions/GetMyDesigns", {
      method: "get",
      headers: {
        Authorization: this.user ? `Bearer ${await this.user?.jwt()}` : null,
      },
    });
    if (response.status !== 200) {
      throw await response.text();
    }

    const payload: IDesign[] = await response.json();

    return payload.map((design) => {
      const parameters = {
        ...design.parameters,
        verticalMaxSize: design.parameters.verticalMaxSize * 1000,
        horizontalMaxSize: design.parameters.horizontalMaxSize * 1000,
        maxLength: design.parameters.maxLength * 1000,
      };
      return { ...design, parameters };
    });
  };

  @action public getMyDesigns = async () => {
    if (!this.user) {
      this.myDesigns = this.getLocalDesigns();
      return;
    }

    this.myDesigns = await this.getServerDesigns();
  };

  @action public fetchDesigns = async (user: User) => {
    try {
      if (!user) {
        await this.getMyDesigns();
        return;
      }

      const tokenExpires = user?.tokenDetails?.().expires_at;

      if (user && tokenExpires < new Date().getTime()) {
        await user?.jwt(true);
      }
      this.user = user;
      await this.getMyDesigns();
    } catch (error) {
      console.log(error);
    }
  };

  @action public calculateHornDesign = async () => {
    try {
      const response = await fetch(".netlify/functions/CalculateHornDesign", {
        method: "post",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: this.user ? `Bearer ${await this.user?.jwt()}` : null,
        },
        body: JSON.stringify({ params: this.params, email: this.email }),
      });

      const payload: IHornDesignResponse = await response.json();

      if (!this.user) {
        let localModels = JSON.parse(sessionStorage.getItem("models")) || [];

        const model = localModels.find((d: IDesign) =>
          isEqual(this.params, d.parameters)
        );

        if (!model) {
          payload?._id &&
            localModels.unshift({
              result: payload,
              parameters: this.params,
              created: new Date(),
            });

          sessionStorage.setItem("models", JSON.stringify(localModels));
        }
      }

      this.selectedDesign = {
        result: payload,
        parameters: this.params,
        created: new Date().toISOString(),
        email: "",
      };
    } catch (error) {
      console.log(error);
      this.alertDialog = {
        title: "Error",
        message:
          "We were unable to calculate the horn with the given parameters. Please try again.",
        error: error?.errorMessage || error?.message || error,
      };

      this.showAlert = true;
    } finally {
      this.setLoading(false);
      this.getMyDesigns();
    }
  };

  @action public setLoading = (setting: boolean) => {
    this.appLoading = setting;
  };

  @action selectDesignsToCompare = (newDesign: IDesign) => {
    const designAlreadySelectedIndex = this.selectedDesigns.findIndex(
      (d) => d.created === newDesign.created
    );

    if (designAlreadySelectedIndex !== -1) {
      this.selectedDesigns.splice(designAlreadySelectedIndex, 1);

      this.selectedDesigns = [...this.selectedDesigns];
      return;
    }

    if (this.selectedDesigns.length === 0) {
      this.selectedDesigns = [newDesign];
      return;
    }

    if (this.selectedDesigns.length >= 2) {
      this.selectedDesigns = [this.selectedDesigns[1], newDesign];
      return;
    }

    this.selectedDesigns = [...this.selectedDesigns, newDesign];
  };

  @computed public get models() {
    const designs = this.selectedDesigns.map((d) => ({
      model: (typeof d.result === "string"
        ? JSON.parse(d.result)
        : d.result) as IHornDesignResponse,
      driverName: d.parameters.driverName,
      geometry: d.parameters.geometry,
    }));

    return [
      {
        model: this.results,
        driverName: this.selectedDesign?.parameters?.driverName,
        geometry: this.selectedDesign?.parameters?.geometry,
      },
      ...designs,
    ];
  }

  @computed public get hasSingleModel() {
    return this.models.length === 1;
  }
}

export const store = new Store();
